import Access from '@advisor/models/store/access/index';
import {returnNoneApi} from '@advisor/util/returnNoneApi';
import exceptionHandler from '@advisor/util/exceptionHandler';
import RwAdvisorService from '@advisor/rwSdk/AdvisorService';
import RwAuthService from '@advisor/rwSdk/AuthService';
import RwOrchestrateService from '@advisor/rwSdk/OrchestrateService';
import {USER_TYPE} from '@advisor/util/constants';
import {addRiaComponentTo} from '@advisor/util/store/paramInjects';

const HTTP_OK = 200;

export const stateObj = () => new Access();

export const getters = {
  // Set full name for user list
  advisorFilterableColumns: (state) => {
    const arr = state.users.map((item) => {
      Object.assign(item, {name: `${item.firstName} ${item.lastName}`});

      return item;
    });

    return arr;
  },
  // roles getters
  rolesWithUsers: (state) => {
    // target component example: @advisor/components/common/RwBadgeRow.vue
    const arr1 = state.roles.filter((row) => row.roleName.trim() !== 'No Access');
    const arr2 = state.roles.filter((row) => row.roleName.trim() === 'No Access');

    // eslint-disable-next-line no-use-before-define
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return [...arr1, ...arr2].map((rol) => ({text: rol.roleName, qty: usersOf(rol.roleName)}));

    // eslint-disable-next-line func-style
    function usersOf(role) {
      const arr = state.users.filter((usr) => usr.role === role);

      return arr.length;
    }
  },
};

export const actions = {
  // permissions:
  // eslint-disable-next-line consistent-return
  async getPermissions({commit, rootState}) {
    try {
      const fullPayload = addRiaComponentTo({}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getRiaRoleAccessComponents(fullPayload).catch((error) => {
        throw error;
      });

      const sdkFiltered = sdkResponse.filter((rol) => rol.isModule);

      return commit('SET_PERMISSIONS', sdkFiltered);
    } catch (error) {
      return exceptionHandler.handleGenericException('access.getPermissions', error);
    }
  },
  // user actions
  // eslint-disable-next-line consistent-return
  async addRiaUser({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.createRiaUser(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve({status: HTTP_OK, data: {data: sdkResponse}});
    } catch (error) {
      return exceptionHandler.handleGenericException('access.addRiaUser', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async editRiaUser({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.updateRiaUser(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve({status: HTTP_OK, data: {data: sdkResponse}});
    } catch (error) {
      return exceptionHandler.handleGenericException('access.editRiaUser', error);
    }
  },
  async deleteRiaUser({rootState}, riaUserId) {
    try {
      const fullPayload = addRiaComponentTo({riaUserId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.deleteRiaUser(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      return exceptionHandler.handleGenericException('access.deleteRiaUser', error, true);
    }
  },
  // eslint-disable-next-line consistent-return
  async getRiaUsers({commit, rootState}, riaId) {
    try {
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getAdvisors(fullPayload).catch((error) => {
        throw error;
      });

      const sdkFiltered = sdkResponse.filter((el) => el.role);

      commit('SET_USERS', sdkFiltered);

      return Promise.resolve(sdkFiltered);
    } catch (error) {
      exceptionHandler.handleGenericException('access.getRiaUsers', error);
    }
  },
  // email verification
  async emailVerification({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo({...payload, userType: USER_TYPE.RIA_USER}, rootState.settings.default);
      const sdkResponse = await RwAuthService.isEmailAvailable(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve({
        status: HTTP_OK,
        data: {available: sdkResponse},
      });
    } catch (error) {
      return exceptionHandler.handleGenericException('access.emailVerification', error);
    }
  },
  // email change verification
  async emailChangeVerification(_, payload) {
    try {
      const response = await RwOrchestrateService.emailChangeVerification(payload);

      returnNoneApi(response);

      return Promise.resolve(response);
    } catch (error) {
      return exceptionHandler.handleGenericException('access.emailChangeVerification', error);
    }
  },
  // user authentication
  async riaUserAuthentication(_, payload) {
    try {
      const response = await RwOrchestrateService.riaUserAuthentication(payload);

      return response;
    } catch (error) {
      return exceptionHandler.handleGenericException('access.riaUserAuthentication', error);
    }
  },
  // get ria user by ria user id
  async getRiaUserbyRiaUserId({rootState}, riaUserId) {
    try {
      const fullPayload = addRiaComponentTo({riaUserId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getRiaUserFromRiaUserId(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve({data: {data: sdkResponse}});
    } catch (error) {
      return exceptionHandler.handleGenericException('access.getRiaUserbyRiaUserId', error);
    }
  },
  // roles
  // eslint-disable-next-line consistent-return
  async getRoles({commit, rootState}) {
    try {
      const fullPayload = addRiaComponentTo({}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getRiaGroupsPrivileges(fullPayload).catch((error) => {
        throw error;
      });

      return commit('SET_ROLES', sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('access.getRoles', error);
    }
  },
  checkTempEmail({commit}, tempEmail) {
    let tempEmailAvailability = false;

    if (tempEmail !== null) {
      tempEmailAvailability = true;
    }

    commit('SET_TempEmailAvailability', tempEmailAvailability);
    commit('SET_TempEmail', tempEmail);
  },
  resetTempEmail({commit}) {
    const tempEmailAvailability = false;

    commit('SET_TempEmailAvailability', tempEmailAvailability);
    commit('SET_TempEmail', null);
  },
  // client email verification
  async clientEmailVerification({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo({...payload, userType: USER_TYPE.CL_USER}, rootState.settings.default);
      const sdkResponse = await RwAuthService.isEmailAvailable(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve({
        status: HTTP_OK,
        data: {available: sdkResponse},
      });
    } catch (error) {
      return exceptionHandler.handleGenericException('access.clientEmailVerification', error);
    }
  },
};

export const mutations = {
  SET_USERS(state, payload) {
    state.users = payload;
  },
  SET_ROLES(state, payload) {
    state.roles = payload;
  },
  SET_PERMISSIONS(state, payload) {
    state.permissions = payload;
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  SET_TempEmailAvailability(state, tempEmailAvailability) {
    state.tempEmailAvailability = tempEmailAvailability;
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  SET_TempEmail(state, tempEmail) {
    state.tempEmail = tempEmail;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters,
  actions,
  mutations,
};
